package ai.wagemix.net.models

import com.fsryan.foundation.models.toEnumValue
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.math.max
import kotlin.math.min

interface WMSharedMixCreate {
    interface Filters {
        val areaId: Int?
        val industryId: Int?
        companion object
    }
    interface PayRange {
        val high: Float
        val low: Float
        companion object
    }
    val filters: Filters
    val hourly: Boolean
    val mixId: String
    val payrange: PayRange?
    val purpose: WMSharedMixPurpose
    companion object
}

interface WMSharedMixResponse {
    val mixId: String
    val shareableUrl: String
    companion object
}

fun WMSharedMixResponse.Companion.kxs(mixId: String, shareableUrl: String): KxsWMSharedMixResponse {
    return KxsWMSharedMixResponse(mixId = mixId, shareableUrl = shareableUrl)
}

fun WMSharedMixResponse.toKxs(): KxsWMSharedMixResponse = when (this) {
    is KxsWMSharedMixResponse -> this
    else -> WMSharedMixResponse.kxs(mixId = mixId, shareableUrl = shareableUrl)
}

fun WMSharedMixCreate.Companion.kxs(
    filters: WMSharedMixCreate.Filters,
    hourly: Boolean,
    mixId: String,
    payrange: WMSharedMixCreate.PayRange?,
    purpose: WMSharedMixPurpose
): KxsWMSharedMixCreate {
    return KxsWMSharedMixCreate(
        filters = filters.toKxs(),
        hourly = hourly,
        mixId = mixId,
        payrange = payrange?.toKxs(),
        _purpose = purpose.stringValue
    )
}

fun WMSharedMixCreate.toKxs(): KxsWMSharedMixCreate = when (this) {
    is KxsWMSharedMixCreate -> this
    else -> WMSharedMixCreate.kxs(
        filters = filters,
        hourly = hourly,
        mixId = mixId,
        payrange = payrange,
        purpose = purpose
    )
}

fun WMSharedMixCreate.Filters.Companion.kxsNone(): KxsWMSharedMixCreate.Filters {
    return kxs(areaId = null, industryId = null)
}

fun WMSharedMixCreate.Filters.Companion.kxsArea(areaId: Int): KxsWMSharedMixCreate.Filters {
    return kxs(areaId = areaId, industryId = null)
}

fun WMSharedMixCreate.Filters.Companion.kxsIndustry(industryId: Int): KxsWMSharedMixCreate.Filters {
    return kxs(areaId = null, industryId = industryId)
}

private fun WMSharedMixCreate.Filters.Companion.kxs(areaId: Int?, industryId: Int?): KxsWMSharedMixCreate.Filters {
    return KxsWMSharedMixCreate.Filters(areaId = areaId, industryId = industryId)
}

fun WMSharedMixCreate.Filters.toKxs(): KxsWMSharedMixCreate.Filters = when (this) {
    is KxsWMSharedMixCreate.Filters -> this
    else -> WMSharedMixCreate.Filters.kxs(areaId = areaId, industryId = industryId)
}

fun WMSharedMixCreate.PayRange.Companion.kxs(high: Float, low: Float): KxsWMSharedMixCreate.PayRange {
    return KxsWMSharedMixCreate.PayRange(high = max(high, low), low = min(low, high))
}

fun WMSharedMixCreate.PayRange.toKxs(): KxsWMSharedMixCreate.PayRange = when (this) {
    is KxsWMSharedMixCreate.PayRange -> this
    else -> WMSharedMixCreate.PayRange.kxs(high = high, low = low)
}

@Serializable
@ConsistentCopyVisibility
data class KxsWMSharedMixCreate internal constructor(
    override val filters: Filters = Filters(),
    override val hourly: Boolean,
    override val mixId: String,
    override val payrange: PayRange? = null,
    @SerialName("purpose") private val _purpose: String
): WMSharedMixCreate {

    override val purpose: WMSharedMixPurpose
        get() = _purpose.toEnumValue(defaultValue = WMSharedMixPurpose.Unknown)

    @Serializable
    @ConsistentCopyVisibility
    data class Filters internal constructor(
        override val areaId: Int? = null,
        override val industryId: Int? = null
    ) : WMSharedMixCreate.Filters

    @Serializable
    @ConsistentCopyVisibility
    data class PayRange internal constructor(override val high: Float, override val low: Float) :
        WMSharedMixCreate.PayRange
}

@Serializable
@ConsistentCopyVisibility
data class KxsWMSharedMixResponse internal constructor(
    override val mixId: String,
    override val shareableUrl: String
): WMSharedMixResponse

/** `PUT /api/v1/sharedMixes/{id}/active`: turns one of the caller's share links on or off. */
interface WMSharedMixActivation {
    val active: Boolean

    companion object
}

fun WMSharedMixActivation.Companion.kxs(active: Boolean): KxsWMSharedMixActivation {
    return KxsWMSharedMixActivation(active = active)
}

fun WMSharedMixActivation.toKxs(): KxsWMSharedMixActivation = when (this) {
    is KxsWMSharedMixActivation -> this
    else -> WMSharedMixActivation.kxs(active = active)
}

@Serializable
@SerialName("WMSharedMixActivation")
@ConsistentCopyVisibility
data class KxsWMSharedMixActivation internal constructor(override val active: Boolean): WMSharedMixActivation
